import classNames from 'classnames'
import React, { useState, useEffect } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import {
  Control,
  Controller,
  ControllerRenderProps,
  UseFormSetValue
} from 'react-hook-form'
import InputMask from 'react-input-mask'
import { useMultiCustomCheck } from '../../hooks'
import { typeOfCoverageList } from '../../hooks/use-coverage'
import { DateHandler, limitLength, states } from '@angle/utils'
import { PlaceAutocomplete } from '../PlaceAutocomplete/PlaceAutocomplete'
import { SectionTitle } from '../SectionTitle'
import { SwitchRow } from '../SwitchRow'
import { IDependentForm } from './../../interfaces/dependent'
import { CheckBoxControl, FooterButtons } from './styles'
import moment from 'moment'
import { DatePicker } from '../DatePicker'

interface RegistrationFormProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  onContinue: Function
  onBack: () => void
  control: Control<IDependentForm>
  isSpouse: string
  healthInsurance: boolean
  getErrors: Function
  formState: any
  getValues: Function
  trigger: Function
  unregister: Function
  setValue: UseFormSetValue<IDependentForm>
}

export const DependentRegistrationForm = ({
  onContinue,
  onBack,
  control,
  isSpouse,
  getErrors,
  formState,
  getValues,
  trigger,
  setValue,
  healthInsurance,
  unregister,
  ...others
}: RegistrationFormProps) => {
  const config = (window as any).runtimeConfig

  const googleApiKey = config?.googleApiKey || '****'

  const [sameAddress, setSameAddress] = useState(false)
  const submitForm = async () => {
    await trigger()

    const hasError = Object.keys(formState.errors).length

    if (!hasError) onContinue(sameAddress)
  }

  const requiredOnlySameAddress = (value) => {
    if (!sameAddress && !value) return false

    return true
  }

  const { defaultChecked, defaultValue, init, inputRef, isCustomChecked } =
    useMultiCustomCheck(
      typeOfCoverageList,
      getValues('carrierInfo.typesOfCoverage') || []
    )

  useEffect(() => {
    console.log(getValues())
  }, [getValues])

  const onCheckCoverage =
    (
      field: ControllerRenderProps<
        IDependentForm,
        'carrierInfo.typesOfCoverage'
      >
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = field.value || []

      if (e.target.checked) {
        const newValue = e.target.value
        field.onChange([...value, newValue])
      } else {
        field.onChange(value.filter((v) => v !== e.target.value))
      }
    }

  const personalInfoError = getErrors('personalInfo') as any
  const carrierInfoError = getErrors('carrierInfo') as any

  const unregisterSpouseFields = () => {
    unregister('personalInfo.personalPhoneNumber')
    unregister('personalInfo.workPhoneNumber')
    unregister('personalInfo.workEmail')
  }

  const unregisterAddressFields = (sameAddress: boolean) => {
    if (sameAddress) {
      unregister('personalInfo.state')
      unregister('personalInfo.streetAddress')
      unregister('personalInfo.streetAddress2')
      unregister('personalInfo.zipCode')
      unregister('personalInfo.city')
    }
  }

  const unregisterCarrierFields = (otherInsurance: boolean) => {
    if (!otherInsurance) {
      unregister('carrierInfo.insurer')
      unregister('carrierInfo.policyNumber')
      unregister('carrierInfo.dateOfCoverage.startAt')
      unregister('carrierInfo.dateOfCoverage.endAt')
      unregister('carrierInfo.typesOfCoverage')
    }
  }

  return (
    <div {...others}>
      <SectionTitle>Dependent's Personal information</SectionTitle>
      <Form>
        <Row className="my-3">
          <Col>
            <h6
              className={classNames({
                'text-danger':
                  personalInfoError.errors?.isSpouse && formState.isSubmitted
              })}
            >
              Is this dependent a spouse/domestic partner or child?
            </h6>
          </Col>
        </Row>
        <Row>
          <Controller
            name="personalInfo.isSpouse"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <CheckBoxControl value="yes" field={field}>
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 35"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="d-inline mr-1"
                  >
                    <path
                      d="M10 2C9.20435 2 8.44129 2.31607 7.87868 2.87868C7.31607 3.44129 7 4.20435 7 5C7 5.79565 7.31607 6.55871 7.87868 7.12132C8.44129 7.68393 9.20435 8 10 8C10.7956 8 11.5587 7.68393 12.1213 7.12132C12.6839 6.55871 13 5.79565 13 5C13 4.20435 12.6839 3.44129 12.1213 2.87868C11.5587 2.31607 10.7956 2 10 2ZM18 2C17.2044 2 16.4413 2.31607 15.8787 2.87868C15.3161 3.44129 15 4.20435 15 5C15 5.79565 15.3161 6.55871 15.8787 7.12132C16.4413 7.68393 17.2044 8 18 8C18.7956 8 19.5587 7.68393 20.1213 7.12132C20.6839 6.55871 21 5.79565 21 5C21 4.20435 20.6839 3.44129 20.1213 2.87868C19.5587 2.31607 18.7956 2 18 2ZM21.002 6.00195C21.002 6.00195 20.925 7.42105 21.752 8.24805C22.579 9.07505 23.998 8.99805 23.998 8.99805C23.998 8.99805 24.075 7.57895 23.248 6.75195C22.421 5.92495 21.002 6.00195 21.002 6.00195ZM9 10C7.346 10 6 11.346 6 13V19C6 19.553 6.448 20 7 20V25.9668C7 26.5368 7.4632 27 8.0332 27C8.5772 27 9.02845 26.5782 9.06445 26.0352L9.50195 20.4648C9.51995 20.2038 9.737 20 10 20C10.263 20 10.48 20.2048 10.498 20.4668L10.9355 26.0352C10.9647 26.474 11.2715 26.8221 11.6738 26.9434C11.7787 26.9802 11.8889 26.9994 12 27H24C24.2652 27 24.5195 26.8946 24.7071 26.7071C24.8946 26.5195 25 26.2652 25 26C25 19.2526 21.692 15.4926 21.0762 14.8223L21.2422 14.1523C21.7644 12.0664 20.1504 10 18 10C16.8296 10 15.8329 10.6213 15.2383 11.5156C15.1881 11.5629 15.1429 11.6153 15.1035 11.6719L14 13.2266V13C14 11.346 12.654 10 11 10H9ZM18 12C18.8976 12 19.5205 12.7979 19.3027 13.668H19.3008L19.0293 14.7578C18.9876 14.9253 18.9899 15.1008 19.0361 15.2671C19.0824 15.4335 19.1708 15.585 19.293 15.707C19.293 15.707 22.4945 19.0748 22.8691 25H13.1211C13.2878 21.7954 14.0455 19.4374 14.8691 17.9961C15.7877 16.3886 16.5996 15.8008 16.5996 15.8008C16.7573 15.6828 16.8766 15.5209 16.9426 15.3353C17.0086 15.1498 17.0184 14.9489 16.9707 14.7578L16.6992 13.668H16.6973C16.6169 13.3469 16.6532 13.0364 16.7734 12.7715C16.7905 12.7436 16.8061 12.7149 16.8203 12.6855C17.0427 12.2801 17.4712 12 18 12Z"
                      className={
                        field.value === 'yes' ? 'fill-primary' : 'fill-muted'
                      }
                    />
                  </svg>
                  Spouse/Domestic Partner
                </CheckBoxControl>

                <CheckBoxControl
                  value="no"
                  field={field}
                  onChange={(e) => {
                    unregisterSpouseFields()
                    field.onChange(e)
                  }}
                >
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 35"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="d-inline mr-1"
                  >
                    <path
                      d="M7.75 0.5C6.02375 0.5 4.625 1.89875 4.625 3.625C4.625 5.35125 6.02375 6.75 7.75 6.75C9.47625 6.75 10.875 5.35125 10.875 3.625C10.875 1.89875 9.47625 0.5 7.75 0.5ZM20.25 0.5C19.4212 0.5 18.6263 0.82924 18.0403 1.41529C17.4542 2.00134 17.125 2.7962 17.125 3.625C17.125 4.4538 17.4542 5.24866 18.0403 5.83471C18.6263 6.42076 19.4212 6.75 20.25 6.75C21.0788 6.75 21.8737 6.42076 22.4597 5.83471C23.0458 5.24866 23.375 4.4538 23.375 3.625C23.375 2.7962 23.0458 2.00134 22.4597 1.41529C21.8737 0.82924 21.0788 0.5 20.25 0.5ZM5.25 8C3.86875 8 2.75 9.11875 2.75 10.5V15.5H4V18H6.5V15.5H9V18H11.5V15.5H12.75V10.5C12.75 9.11875 11.6312 8 10.25 8H5.25ZM19.7715 8C18.8177 8 17.9614 8.53059 17.5352 9.38184L14.4785 15.5H16.5V18H19V15.5H21.5V18H24V15.5H26.0215L22.9648 9.38184C22.5386 8.53059 21.6823 8 20.7285 8H19.7715ZM1.57813 19.25C1.55188 19.25 1.52625 19.2497 1.5 19.2622H1.46338C1.39838 19.2622 1.34527 19.2753 1.28027 19.2891C1.24152 19.3016 1.18873 19.3156 1.13623 19.3281C1.13623 19.3281 1.12313 19.3403 1.10938 19.3403H1.09717C1.08467 19.3528 1.05965 19.3534 1.0459 19.3672C0.954649 19.4059 0.863419 19.4572 0.784669 19.5234C0.549669 19.7059 0.366563 19.9812 0.289063 20.2949C0.276563 20.3074 0.276856 20.3215 0.276856 20.334C0.263106 20.414 0.250001 20.4796 0.250001 20.5708V20.583C0.250001 20.6618 0.249708 20.7264 0.262208 20.8052C0.274708 20.9102 0.30004 21.002 0.35254 21.0933C0.35254 21.1058 0.354688 21.1183 0.367188 21.1445C0.537188 21.3533 0.717813 21.5489 0.914063 21.7451L0.92627 21.7598C1.44752 22.2698 2.07281 22.739 2.78906 23.144C2.98406 23.2753 3.1809 23.3808 3.38965 23.4858C3.4159 23.4858 3.42932 23.498 3.45557 23.498C3.52057 23.5105 3.59779 23.5249 3.67529 23.5249H3.71436C3.79311 23.5249 3.85902 23.5118 3.93652 23.498H3.98779C4.06654 23.4855 4.13246 23.4584 4.20996 23.4321C4.22246 23.4196 4.24992 23.419 4.26367 23.4053C4.27617 23.4053 4.28779 23.3946 4.30029 23.3809C4.41779 23.3284 4.50934 23.2619 4.60059 23.1831C4.65309 23.1444 4.70434 23.0931 4.75684 23.0269L4.76904 23.0146C4.88654 22.8571 4.97902 22.6607 5.03027 22.4507V22.4385C5.04277 22.3472 5.05713 22.2685 5.05713 22.1772C5.05963 22.1397 5.05986 22.0879 5.04736 22.0479C5.04736 22.0091 5.03426 21.956 5.02051 21.916C5.02051 21.8635 5.0077 21.8123 4.98145 21.7598C4.92895 21.5773 4.8258 21.4052 4.6958 21.2739C4.6833 21.2477 4.64148 21.2096 4.61523 21.1958C4.58898 21.1695 4.56365 21.1439 4.5249 21.1177C4.5124 21.1052 4.49988 21.0908 4.47363 21.0908L4.19775 20.9346C3.53275 20.5558 2.97463 20.1234 2.55713 19.6797C2.54463 19.6534 2.50281 19.6129 2.47656 19.5991C2.32031 19.4416 2.12498 19.3391 1.91748 19.2866C1.90498 19.2866 1.87996 19.2744 1.86621 19.2744H1.85156C1.78656 19.2632 1.69623 19.25 1.60498 19.25H1.57813ZM26.3975 19.3525C26.3712 19.3525 26.3431 19.3547 26.3169 19.3672H26.2778C26.2128 19.3672 26.1622 19.3779 26.0972 19.3916C26.0584 19.4041 26.0056 19.4182 25.9531 19.4307C25.9531 19.4307 25.94 19.4429 25.9263 19.4429H25.9141C25.9016 19.4554 25.8741 19.456 25.8603 19.4697C25.7691 19.5085 25.6779 19.5622 25.5991 19.6284C25.0254 20.0597 24.3616 20.4502 23.6191 20.8027C23.6191 20.8027 23.606 20.8174 23.5923 20.8174H23.5801C23.5676 20.8299 23.5401 20.828 23.5264 20.8418C23.1614 21.0118 22.877 21.3532 22.772 21.772C22.7595 21.7845 22.7598 21.7985 22.7598 21.811C22.7473 21.8898 22.7329 21.9542 22.7329 22.0454V22.0576C22.7329 22.1364 22.7326 22.201 22.7451 22.2798C22.7576 22.3848 22.7829 22.4766 22.8354 22.5679C22.8354 22.5941 22.8486 22.6195 22.8623 22.6582C23.0186 22.972 23.3072 23.2219 23.646 23.3394C23.6985 23.3519 23.7497 23.3659 23.8022 23.3784C23.8672 23.3909 23.9445 23.4028 24.022 23.4028H24.061C24.1398 23.4028 24.2057 23.3922 24.2832 23.3784H24.2954C24.5167 23.2734 24.7385 23.1685 24.9473 23.0635C25.651 22.711 26.3007 22.3305 26.8882 21.938C27.0707 21.7942 27.2526 21.6627 27.4351 21.5327L27.4497 21.5205C27.5672 21.363 27.6584 21.169 27.7109 20.959V20.9468C27.7234 20.8555 27.7353 20.7768 27.7353 20.6855C27.7366 20.6493 27.7381 20.5973 27.7256 20.5586C27.7256 20.5198 27.7125 20.4668 27.6987 20.4268C27.6987 20.3743 27.6859 20.3206 27.6597 20.2681C27.5297 19.8368 27.1769 19.5091 26.7344 19.3916C26.7219 19.3916 26.6969 19.3794 26.6831 19.3794H26.6709C26.6059 19.3669 26.5131 19.3525 26.4219 19.3525H26.3975ZM19.1758 22.3091C19.1495 22.3091 19.1215 22.3088 19.0952 22.3213H19.0586L18.7046 22.3994H18.6924C17.9886 22.5432 17.2593 22.6625 16.5293 22.7412H16.5024C16.4762 22.7412 16.4506 22.7409 16.4243 22.7534C16.3456 22.7534 16.2796 22.7641 16.2021 22.7778C15.7196 22.8828 15.3296 23.2761 15.2134 23.7861C15.2009 23.7986 15.2012 23.8103 15.2012 23.8228C15.1887 23.9015 15.1743 23.9683 15.1743 24.0596V24.0718C15.1743 24.1505 15.174 24.2152 15.1865 24.2939C15.2653 24.7777 15.6162 25.1973 16.085 25.3535C16.1375 25.366 16.1887 25.3776 16.2412 25.3901C16.515 25.3776 16.7756 25.3386 17.0493 25.2998C17.7531 25.2211 18.4443 25.1186 19.1343 24.9873C19.343 24.9348 19.5505 24.8958 19.7593 24.8433C20.1368 24.6733 20.398 24.3318 20.4893 23.9131V23.9009C20.5018 23.8096 20.5161 23.7309 20.5161 23.6396C20.5174 23.6034 20.5177 23.5514 20.5039 23.5127C20.5039 23.4739 20.4908 23.4209 20.477 23.3809C20.477 23.3284 20.4642 23.2771 20.438 23.2246C20.308 22.7934 19.9552 22.4656 19.5127 22.3481C19.5002 22.3481 19.4752 22.3359 19.4614 22.3359H19.4492C19.3842 22.3234 19.2914 22.3091 19.2002 22.3091H19.1758ZM8.57764 22.519C7.96514 22.519 7.44299 22.9508 7.31299 23.5396C7.30049 23.5521 7.30078 23.5661 7.30078 23.5786C7.28828 23.6574 7.27393 23.7339 7.27393 23.8252C7.27393 24.4527 7.71641 24.9894 8.31641 25.1069C9.15016 25.2769 10.0254 25.3959 10.9116 25.4609C11.1204 25.4747 11.3422 25.4875 11.5635 25.5H11.6001C12.1213 25.395 12.5388 24.9764 12.6426 24.4526V24.4404L12.6572 24.0498C12.6072 23.4873 12.2022 23.029 11.6685 22.8853C11.5247 22.8853 11.3947 22.8721 11.251 22.8584C10.4297 22.7934 9.62107 22.6896 8.85107 22.5459H8.83887C8.76012 22.5334 8.66889 22.519 8.57764 22.519Z"
                      className={
                        field.value === 'no' ? 'fill-primary' : 'fill-muted'
                      }
                    />
                  </svg>
                  Child
                </CheckBoxControl>
              </>
            )}
          />
        </Row>
        {personalInfoError.errors?.isSpouse?.type === 'required' && (
          <span className="text-danger">You need to choose one</span>
        )}
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label
                className={classNames({
                  'text-danger':
                    personalInfoError.errors?.firstName && formState.isSubmitted
                })}
              >
                First Name
              </Form.Label>
              <Controller
                name="personalInfo.firstName"
                defaultValue=""
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="First Name"
                    isInvalid={
                      personalInfoError.errors?.firstName?.type === 'required'
                    }
                  />
                )}
              />
              {personalInfoError.errors?.firstName?.type === 'required' && (
                <span className="text-danger">First Name is required</span>
              )}
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label
                className={classNames({
                  'text-danger':
                    personalInfoError.errors?.lastName && formState.isSubmitted
                })}
              >
                Last Name
              </Form.Label>
              <Controller
                name="personalInfo.lastName"
                rules={{ required: true }}
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="text"
                    placeholder="Last Name"
                    isInvalid={
                      personalInfoError.errors?.lastName?.type === 'required'
                    }
                  />
                )}
              />
              {personalInfoError.errors?.lastName?.type === 'required' && (
                <span className="text-danger">Last Name is required</span>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label
                className={classNames({
                  'text-danger':
                    personalInfoError.errors?.gender && formState.isSubmitted
                })}
              >
                What gender does the dependent identify as?
              </Form.Label>
              <Controller
                name="personalInfo.identifiedGender"
                rules={{ required: true }}
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    as="select"
                    placeholder="Select gender"
                    className={!field.value ? 'text-muted' : ''}
                    isInvalid={
                      personalInfoError.errors?.gender?.type === 'required'
                    }
                  >
                    <option value="" disabled hidden>
                      Select gender
                    </option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non_binary">Non-binary</option>
                    <option value="non_conforming">Non-conforming</option>
                  </Form.Control>
                )}
              />
              {personalInfoError.errors?.gender?.type === 'required' && (
                <span className="text-danger">
                  Identified Gender is required
                </span>
              )}
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label
                className={classNames({
                  'text-danger':
                    personalInfoError.errors?.gender && formState.isSubmitted
                })}
              >
                Sex (Gender at Birth)
              </Form.Label>
              <Controller
                name="personalInfo.gender"
                rules={{ required: true }}
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    as="select"
                    placeholder="Select gender"
                    className={!field.value ? 'text-muted' : ''}
                    isInvalid={
                      personalInfoError.errors?.gender?.type === 'required'
                    }
                  >
                    <option value="" disabled hidden>
                      Select gender
                    </option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </Form.Control>
                )}
              />
              {personalInfoError.errors?.gender?.type === 'required' && (
                <span className="text-danger">Gender is required</span>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            {/* Had to make the form group flex, and the label w-100, in order to keep the flatpickr input in correct placement while the flatpickr has static set to true. Not sure why bu setting static to true is the only way to rpevent the month flickering issue */}
            <Form.Group className="d-flex flex-column">
              <Form.Label
                style={{ minWidth: '100%' }}
                className={classNames({
                  'text-danger':
                    personalInfoError.errors?.dateOfBirth &&
                    formState.isSubmitted
                })}
              >
                Date Of Birth
              </Form.Label>
              <Controller
                name="personalInfo.dateOfBirth"
                key="personalInfo.dateOfBirth"
                rules={{
                  required: true,
                  validate: {
                    notFuture: (value) => {
                      if (moment(value).isSameOrBefore(new Date())) return true
                      else return false
                    }
                  }
                }}
                control={control}
                render={({ field }) => <DatePicker {...field} />}
              />
              {personalInfoError.errors?.dateOfBirth?.type === 'required' && (
                <span className="text-danger">Date Of Birth is required</span>
              )}
              {personalInfoError.errors?.dateOfBirth?.type === 'notFuture' && (
                <span className="text-danger">
                  Date of Birth cannot be in the future.{' '}
                </span>
              )}
            </Form.Group>
          </Col>

          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label
                className={classNames({
                  'text-danger':
                    personalInfoError.errors?.ssn && formState.isSubmitted
                })}
              >
                Social Security Number
              </Form.Label>
              <Controller
                name="personalInfo.ssn"
                rules={{
                  required: true,
                  validate: {
                    correctPattern: (value) => {
                      if (String(value).match(/[a-zA-Z]+/g)) return false
                      if (
                        (String(value)
                          ?.match(/[0-9]+/g)
                          ?.join('')?.length as any) < 9
                      )
                        return false
                      return true
                    }
                  }
                }}
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    as={InputMask}
                    mask="999-99-9999"
                    placeholder="Enter dependent's SSN"
                    className={!field.value ? 'text-muted' : ''}
                    isInvalid={
                      personalInfoError.errors?.ssn?.type === 'required'
                    }
                  />
                )}
              />
              {personalInfoError.errors?.ssn &&
                formState.isSubmitted &&
                personalInfoError.errors?.ssn?.type === 'correctPattern' && (
                  <span className="text-danger">SSN must be 9 digits.</span>
                )}
              {personalInfoError.errors?.ssn?.type === 'required' && (
                <span className="text-danger">
                  Social Security Number is required
                </span>
              )}
            </Form.Group>
            <Form.Group>
              <Form.Label
                className={classNames({
                  'text-danger':
                    personalInfoError.errors?.personalEmail &&
                    formState.isSubmitted
                })}
              >
                Personal Email <span>{isSpouse === 'no' && '(Optional)'}</span>
              </Form.Label>
              <Controller
                name="personalInfo.personalEmail"
                control={control}
                rules={{
                  required: isSpouse === 'yes'
                }}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    onChange={(e) => {
                      field.onChange(e)
                      trigger('personalInfo.personalEmail')
                    }}
                    type="email"
                    placeholder="Dependent's email address"
                    className={!field.value ? 'text-muted' : ''}
                    isInvalid={
                      personalInfoError.errors?.personalEmail?.type ===
                      'required'
                    }
                  />
                )}
              />
              {personalInfoError.errors?.personalEmail?.type === 'required' && (
                <span className="text-danger">Email is required</span>
              )}
            </Form.Group>
            {isSpouse === 'yes' && (
              <Form.Group>
                <Form.Label
                  className={classNames({
                    'text-danger':
                      personalInfoError.errors?.personalPhoneNumber &&
                      formState.isSubmitted
                  })}
                >
                  Phone Number
                </Form.Label>
                <Controller
                  name="personalInfo.personalPhoneNumber"
                  rules={{
                    required: true,
                    validate: {
                      noLessThan10: (value) => {
                        if (
                          String(value?.match(/[0-9]+/g)?.join('')).length < 10
                        )
                          return false
                        return true
                      }
                    }
                  }}
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as={InputMask}
                      mask="999 999-9999"
                      type="tel"
                      placeholder="Personal Phone Number"
                      isInvalid={
                        personalInfoError.errors?.personalPhoneNumber?.type ===
                        'required'
                      }
                    />
                  )}
                />
                {personalInfoError.errors?.personalPhoneNumber &&
                  formState.isSubmitted &&
                  personalInfoError.errors?.personalPhoneNumber?.type ===
                    'noLessThan10' && (
                    <span className="text-danger">
                      Phone number is invalid.
                    </span>
                  )}
                {personalInfoError.errors?.personalPhoneNumber?.type ===
                  'required' && (
                  <span className="text-danger">Phone Number is required</span>
                )}
              </Form.Group>
            )}
          </Col>
        </Row>

        {isSpouse === 'yes' && (
          <small className="text-primary font-weight-bold">
            * Please note that a unique email is required in order to grant a
            dependent with their own account and log in.{' '}
          </small>
        )}
        <hr />

        <SectionTitle className="my-5">
          Dependent's Contact Information
        </SectionTitle>

        <Form.Check
          onChange={(e) => {
            unregisterAddressFields(e.target.checked)
            setSameAddress(e.target.checked)
          }}
          className="my-4"
          name="sameAddress"
          id="sameAddress"
          label="Address is same as my address"
          checked={sameAddress}
        />

        {!sameAddress && (
          <Row>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label
                  className={classNames({
                    'text-danger':
                      personalInfoError.errors?.streetAddress &&
                      formState.isSubmitted
                  })}
                >
                  Street Address
                </Form.Label>

                <Controller
                  name="personalInfo.streetAddress"
                  rules={{ required: true }}
                  control={control}
                  render={({ field }) => (
                    <PlaceAutocomplete
                      ref={field.ref}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      className="form-control"
                      placeholder="Street Address"
                      defaultValue={field.value}
                      apiKey={googleApiKey}
                      onPlaceSelection={({
                        address,
                        city,
                        zip,
                        stateShort
                      }) => {
                        setValue('personalInfo.streetAddress', address)
                        setValue('personalInfo.state', stateShort)
                        setValue('personalInfo.city', city)
                        setValue('personalInfo.zipCode', zip)
                      }}
                    />
                  )}
                />
                {personalInfoError.errors?.streetAddress?.type ===
                  'required' && (
                  <span className="text-danger">
                    Street Address is required
                  </span>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label
                  className={classNames({
                    'text-danger':
                      personalInfoError.errors?.streetAddress2 &&
                      formState.isSubmitted
                  })}
                >
                  Street Address Line 2
                </Form.Label>
                <Controller
                  name="personalInfo.streetAddress2"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      placeholder="Street Address Line 2 "
                      isInvalid={
                        personalInfoError.errors?.streetAddress2 &&
                        formState.isSubmitted
                      }
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label
                  className={classNames({
                    'text-danger':
                      personalInfoError.errors?.state && formState.isSubmitted
                  })}
                >
                  State
                </Form.Label>
                <Controller
                  name="personalInfo.state"
                  rules={{ validate: requiredOnlySameAddress }}
                  defaultValue=""
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as="select"
                      placeholder="Select a state"
                      className={!field.value ? 'text-muted' : ''}
                      isInvalid={
                        personalInfoError.errors?.state?.type === 'validate'
                      }
                    >
                      <option value="" disabled hidden>
                        Select a state
                      </option>

                      {Object.keys(states).map((s) => (
                        <option key={s} value={s}>
                          {(states as any)[s]}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                />
                {personalInfoError.errors?.state?.type === 'validate' && (
                  <span className="text-danger">State is required</span>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label
                  className={classNames({
                    'text-danger':
                      personalInfoError.errors?.city && formState.isSubmitted
                  })}
                >
                  City
                </Form.Label>
                <Controller
                  name="personalInfo.city"
                  rules={{ validate: requiredOnlySameAddress }}
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder="City"
                      className={!field.value ? 'text-muted' : ''}
                      isInvalid={
                        personalInfoError.errors?.city?.type === 'validate'
                      }
                    />
                  )}
                />
                {personalInfoError.errors?.city?.type === 'validate' && (
                  <span className="text-danger">City is required</span>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label
                  className={classNames({
                    'text-danger':
                      personalInfoError.errors?.zipCode && formState.isSubmitted
                  })}
                >
                  Zip Code
                </Form.Label>
                <Controller
                  name="personalInfo.zipCode"
                  rules={{
                    validate: {
                      requiredOnlySameAddress,
                      validateNumber: (value) =>
                        !Number.isNaN(value) && value?.length === 5
                    }
                  }}
                  control={control}
                  render={({ field: { onChange, ...field } }) => (
                    <Form.Control
                      {...field}
                      onChange={(e) => limitLength(e, 5, onChange)}
                      type="text"
                      placeholder="Zip Code"
                      isInvalid={
                        personalInfoError.errors?.zipCode?.type ===
                        'requiredOnlySameAddress'
                      }
                    />
                  )}
                />
                {personalInfoError.errors?.zipCode?.type ===
                  'requiredOnlySameAddress' && (
                  <span className="text-danger">Zip Code is required</span>
                )}
              </Form.Group>
            </Col>
          </Row>
        )}

        <hr />

        <SectionTitle>Current Insurance Carrier Information</SectionTitle>
        <SwitchRow
          name="healthInsurance"
          label="Does the dependent have insurance coverage that will coverage continue?"
          className="py-3"
          control={control}
          sideEffect={unregisterCarrierFields}
        />
        {healthInsurance && (
          <>
            <Row>
              <Col xs="12" sm="6">
                <Form.Group>
                  <Form.Label
                    className={classNames({
                      'text-danger':
                        carrierInfoError.errors?.insurer &&
                        formState.isSubmitted
                    })}
                  >
                    Insurance Carrier
                  </Form.Label>
                  <Controller
                    name="carrierInfo.insurer"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        onChange={(e) => {
                          field.onChange(e)
                          trigger('carrierInfo.coverageContinue')
                        }}
                        placeholder="Insurance Carrier"
                        className={!field.value ? 'text-muted' : ''}
                        isInvalid={
                          carrierInfoError.errors?.insurer?.type === 'required'
                        }
                      />
                    )}
                  />
                  {carrierInfoError.errors?.insurer?.type === 'required' && (
                    <span className="text-danger">
                      Insurance Carrier is required
                    </span>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="my-2">
              <Col>
                <Form.Group>
                  <Form.Label>
                    Type of coverage (Check all that apply)
                  </Form.Label>
                  <Controller
                    name="carrierInfo.typesOfCoverage"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => {
                      const { onCheckChanges, onInputBlur } = init(
                        field.value,
                        field.onChange
                      )

                      return (
                        <div>
                          {typeOfCoverageList.map((type, i) => (
                            <Form.Check
                              key={i}
                              {...field}
                              onChange={onCheckCoverage(field)}
                              className="my-2"
                              id={`coverage-${type.value}`}
                              value={type.value}
                              label={type.text}
                            />
                          ))}

                          <Form.Check
                            {...field}
                            className="my-2 d-inline"
                            id={'coverage-custom'}
                            onChange={onCheckChanges}
                            defaultChecked={defaultChecked}
                          />
                          <Form.Control
                            style={{ minWidth: 150, width: '27%' }}
                            size="sm"
                            className="ml-2 d-inline"
                            disabled={!isCustomChecked}
                            placeholder="Other"
                            ref={inputRef}
                            onBlur={onInputBlur}
                            defaultValue={defaultValue}
                          />
                        </div>
                      )
                    }}
                  />
                  {carrierInfoError.errors?.typesOfCoverage?.type ===
                    'required' && (
                    <span className="text-danger">
                      type of coverage is required
                    </span>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <span className="text-danger">
              {formState.isSubmitted && carrierInfoError.message}
            </span>
          </>
        )}
        <FooterButtons className="mb-5">
          <Button variant="link" onClick={onBack}>
            Cancel
          </Button>
          <div>
            <Button id="save-dependent" onClick={submitForm}>
              Continue
            </Button>
          </div>
        </FooterButtons>
      </Form>
    </div>
  )
}

export default DependentRegistrationForm
